/**
 * The tab strip both tab hosts draw — the main window's (MainWindowTabs.tsx)
 * and a tabbed pop-out window's (AppTabsHost.tsx). State lives in the host;
 * this draws it and turns gestures into the host's actions:
 *
 *  - Accessible tablist: roving tabindex, ←/→/Home/End move focus,
 *    Enter/Space activate, Delete closes, Shift+F10 / the context-menu key
 *    (or right click) open the tab's menu. Moves and pins are announced.
 *  - Drag to reorder, with a drop indicator; the strip scrolls when a drag
 *    nears its edge. Pinned tabs stay left (lib/tabStrip.ts clamps every
 *    move); every tab — the main window's home tab too — can be reordered.
 *  - Drag a tab onto ANOTHER window's strip to move it there, or drop it well
 *    below the strip / outside the window to tear it off into a new window
 *    (lib/tabMove.ts has the protocol).
 *  - Per-tab menu: host extras, Move to ▸ (new window / main window / each
 *    other tabbed window), Move left / right / to start / to end, Pin / Unpin,
 *    Close other tabs, Close.
 */
import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppWindow,
  ArrowLeft,
  ArrowLeftToLine,
  ArrowRight,
  ArrowRightToLine,
  ChevronDown,
  Home,
  MoveRight,
  Pin,
  PinOff,
  X,
  XCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  TAB_DRAG_MIME,
  TAB_LOCAL_DRAG_MIME,
  autoScrollDelta,
  canMoveTab,
  dragCarriesTab,
  dragEndPoint,
  dropGapFromPointer,
  encodeTabDragPayload,
  gapToIndex,
  isDragOut,
  parseTabDragPayload,
  setTabPinned,
  type TabDragPayload,
} from '@/lib/tabStrip';
import type { MoveTarget } from '@/lib/tabMove';

export interface StripTab {
  uid: string;
  /** appTabKey of the page (data-tab-key). */
  key: string;
  label: string;
  pinned: boolean;
  /** The main window's primary (home) tab: drawn with a home icon. Reorders and pins like any tab. */
  home?: boolean;
  closable: boolean;
  /** Can go to another window (everything but the primary tab). */
  movable: boolean;
  domId: string;
  panelId: string;
  testId: string;
  closeTestId: string;
  menuTestId: string;
}

export interface TabStripProps {
  tabs: StripTab[];
  activeUid: string | null;
  ariaLabel: string;
  /** Prefix of the menu items' test ids, e.g. 'main-tab' → 'main-tab-move-left'. */
  testIdPrefix: string;
  isDesktop: boolean;
  variant: 'main' | 'popout';
  onActivate: (uid: string) => void;
  onClose: (uid: string) => void;
  onMoveTo: (uid: string, index: number) => void;
  onPin: (uid: string, pinned: boolean) => void;
  onCloseOthers?: (uid: string) => void;
  /** Cross-window moves. */
  onMoveToNewWindow: (uid: string) => void;
  onMoveToTarget: (uid: string, target: MoveTarget) => void;
  listTargets: () => Promise<MoveTarget[]>;
  startDrag: (uid: string) => TabDragPayload | null;
  /** A tab from another window was dropped here at gap `index`. */
  onDropForeign: (payload: TabDragPayload, index: number) => void;
  /** Our tab was dropped off the strip: tear it off into its own window. */
  onDragOut: (uid: string, nonce: string | null) => void;
  /** Items the host puts at the top of a tab's menu (e.g. Duplicate). */
  menuExtras?: (tab: StripTab) => ReactNode;
  /** Buttons the host puts on the tab before its × (e.g. the pop-out button). */
  tabExtras?: (tab: StripTab) => ReactNode;
  /** The "+" control, drawn after the tabs. */
  addControl?: ReactNode;
  className?: string;
  style?: CSSProperties;
  testId?: string;
}

interface DragState {
  uid: string;
  nonce: string | null;
  /** Dropped on this strip (a reorder), so dragend must not tear it off. */
  handled: boolean;
}

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

export function TabStrip(props: TabStripProps) {
  const {
    tabs,
    activeUid,
    ariaLabel,
    testIdPrefix: p,
    isDesktop,
    variant,
    onActivate,
    onClose,
    onMoveTo,
    onPin,
    onCloseOthers,
    onMoveToNewWindow,
    onMoveToTarget,
    listTargets,
    startDrag,
    onDropForeign,
    onDragOut,
    menuExtras,
    tabExtras,
    addControl,
  } = props;
  const { t } = useTranslation();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [focusUid, setFocusUid] = useState<string | null>(null);
  const [targets, setTargets] = useState<MoveTarget[]>([]);
  const [drop, setDrop] = useState<{ gap: number; x: number } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const tabEls = useRef(new Map<string, HTMLDivElement>());
  const drag = useRef<DragState | null>(null);
  const lastMoved = useRef<string | null>(null);
  /** Pinning reorders too; its own announcement says enough. */
  const quietReorder = useRef(false);
  const prevOrder = useRef<string[]>(tabs.map((x) => x.uid));
  const isMac = isDesktop || (typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform));
  const mod = isMac ? '⌘' : 'Ctrl+';
  const onlyOne = tabs.length < 2;

  // Roving tabindex: the focused tab, else the active one, else the first.
  const rovingUid =
    (focusUid && tabs.some((x) => x.uid === focusUid) ? focusUid : null) ??
    (activeUid && tabs.some((x) => x.uid === activeUid) ? activeUid : tabs[0]?.uid ?? null);

  // Announce reorders (drag, menu, keyboard — including the host's window-level shortcut).
  useEffect(() => {
    const order = tabs.map((x) => x.uid);
    const prev = prevOrder.current;
    prevOrder.current = order;
    const sameSet = prev.length === order.length && prev.every((u) => order.includes(u));
    if (!sameSet || prev.every((u, i) => u === order[i])) return;
    if (quietReorder.current) {
      quietReorder.current = false;
      lastMoved.current = null;
      return;
    }
    const uid = lastMoved.current && order.includes(lastMoved.current) ? lastMoved.current : activeUid;
    lastMoved.current = null;
    if (!uid) return;
    const at = order.indexOf(uid);
    if (at < 0 || prev.indexOf(uid) === at) return;
    const tab = tabs[at];
    setAnnouncement(
      t('tabStrip.moved_announce', {
        defaultValue: '{{label}} moved to position {{position}} of {{count}}',
        label: tab.label,
        position: at + 1,
        count: tabs.length,
      }),
    );
  }, [tabs, activeUid, t]);

  // A dragged tab that left this strip (moved to another window) never sees its dragend here.
  useEffect(() => {
    if (drag.current && !tabs.some((x) => x.uid === drag.current?.uid)) drag.current = null;
  }, [tabs]);

  // Whichever way a tab's menu opened (chevron, right click, keyboard), refresh "Move to ▸".
  const menuTab = menuFor ? tabs.find((x) => x.uid === menuFor) : undefined;
  const menuMovable = Boolean(menuTab?.movable);
  useEffect(() => {
    if (!menuFor || !menuMovable) return;
    let live = true;
    listTargets()
      .then((list) => {
        if (live) setTargets(list);
      })
      .catch(() => {
        if (live) setTargets([]);
      });
    return () => {
      live = false;
    };
  }, [menuFor, menuMovable, listTargets]);

  const focusTab = (uid: string) => {
    setFocusUid(uid);
    // After React has reordered the DOM.
    const run = () => tabEls.current.get(uid)?.focus();
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
    else run();
  };

  const move = (uid: string, index: number, viaKeyboard = false) => {
    lastMoved.current = uid;
    onMoveTo(uid, index);
    if (viaKeyboard) focusTab(uid);
  };

  const pin = (tab: StripTab, on: boolean) => {
    lastMoved.current = tab.uid;
    const after = setTabPinned(tabs, tab.uid, on);
    quietReorder.current = after.some((x, i) => x.uid !== tabs[i]?.uid);
    onPin(tab.uid, on);
    setAnnouncement(
      on
        ? t('tabStrip.pinned_announce', { defaultValue: '{{label}} pinned', label: tab.label })
        : t('tabStrip.unpinned_announce', { defaultValue: '{{label}} unpinned', label: tab.label }),
    );
  };

  // ------------------------------------------------------------ drag and drop
  /** The gap (0…n) a drag over / drop at this event lands in. */
  const gapFor = (e: DragEvent<HTMLElement>): number => {
    const el = (e.target as HTMLElement | null)?.closest?.('[role="tab"]') as HTMLElement | null;
    const order = tabs.map((x) => tabEls.current.get(x.uid));
    const idx = el ? order.indexOf(el as HTMLDivElement) : -1;
    if (idx >= 0) {
      const r = el!.getBoundingClientRect();
      return idx + (e.clientX > r.left + r.width / 2 ? 1 : 0);
    }
    const rects = order.map((x) => (x ? x.getBoundingClientRect() : { left: 0, width: 0 }));
    return dropGapFromPointer(rects, e.clientX);
  };

  /** Indicator x (inside the scrolling list) for a gap. */
  const indicatorX = (gap: number): number => {
    const list = listRef.current;
    if (!list) return 0;
    const base = list.getBoundingClientRect().left - list.scrollLeft;
    const before = tabs[gap] ? tabEls.current.get(tabs[gap].uid) : null;
    if (before) return before.getBoundingClientRect().left - base - 2;
    const last = tabs.length ? tabEls.current.get(tabs[tabs.length - 1].uid) : null;
    return last ? last.getBoundingClientRect().right - base + 1 : 0;
  };

  const onListDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!drag.current && !dragCarriesTab(e.dataTransfer?.types)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const gap = gapFor(e);
    if (!drop || drop.gap !== gap) setDrop({ gap, x: indicatorX(gap) });
    const list = listRef.current;
    if (list) {
      const r = list.getBoundingClientRect();
      const dx = autoScrollDelta(e.clientX, r);
      if (dx && list.scrollWidth > list.clientWidth) list.scrollLeft += dx;
    }
  };

  /**
   * Accept the drag on ENTER as well as over. The HTML drag-and-drop model only
   * makes an element the drop target if its dragenter is cancelled; Chrome lets
   * a cancelled dragover make up for it, Safari / WebKit does not. There,
   * entering a tab's buttons (its menu chevron, its ×) left the drag with no
   * target — no more dragover, no drop — so a tab dropped on the right part of
   * another tab silently stayed where it was.
   */
  const onListDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!drag.current && !dragCarriesTab(e.dataTransfer?.types)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };

  const onListDragLeave = (e: DragEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !listRef.current?.contains(next)) setDrop(null);
  };

  const onListDrop = (e: DragEvent<HTMLDivElement>) => {
    const local = drag.current;
    const raw = e.dataTransfer?.getData?.(TAB_DRAG_MIME);
    if (!local && !raw) return;
    e.preventDefault();
    const gap = drop?.gap ?? gapFor(e);
    setDrop(null);
    if (local) {
      local.handled = true;
      const from = tabs.findIndex((x) => x.uid === local.uid);
      if (from >= 0) move(local.uid, gapToIndex(from, gap));
      return;
    }
    const payload = parseTabDragPayload(raw);
    if (payload) onDropForeign(payload, gap);
  };

  const onTabDragStart = (e: DragEvent<HTMLDivElement>, tab: StripTab) => {
    // Only a tab that can go to another window carries the cross-window payload.
    const payload = tab.movable ? startDrag(tab.uid) : null;
    drag.current = { uid: tab.uid, nonce: payload?.nonce ?? null, handled: false };
    if (menuFor !== null) setMenuFor(null);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try {
        // Firefox starts no drag without data; this one is for this strip only.
        e.dataTransfer.setData(TAB_LOCAL_DRAG_MIME, tab.uid);
        if (payload) e.dataTransfer.setData(TAB_DRAG_MIME, encodeTabDragPayload(payload));
      } catch {
        /* some engines refuse custom types: the drag still reorders in this strip */
      }
    }
  };

  const onTabDragEnd = (e: DragEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    setDrop(null);
    if (!d || d.handled) return;
    // Another strip (another window) took it: that window acks, and the tab leaves here then.
    const effect = e.dataTransfer?.dropEffect;
    if (effect && effect !== 'none') return;
    const list = listRef.current;
    if (!list || typeof window === 'undefined') return;
    const point = dragEndPoint(e, window);
    if (isDragOut(point, list.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight })) {
      onDragOut(d.uid, d.nonce);
    }
  };

  // ------------------------------------------------------------ keyboard
  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>, tab: StripTab, i: number) => {
    const noMods = !e.altKey && !e.ctrlKey && !e.metaKey;
    if (e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      // Move THIS (focused) tab — and keep the window-level shortcut from moving the active one too.
      e.preventDefault();
      e.stopPropagation();
      move(tab.uid, i + (e.key === 'ArrowLeft' ? -1 : 1), true);
      return;
    }
    if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
      e.preventDefault();
      setMenuFor(tab.uid);
      return;
    }
    if (!noMods || e.shiftKey) return;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        e.preventDefault();
        const n = tabs.length;
        const next = tabs[(i + (e.key === 'ArrowLeft' ? -1 : 1) + n) % n];
        if (next) focusTab(next.uid);
        return;
      }
      case 'Home':
      case 'End': {
        e.preventDefault();
        const next = e.key === 'Home' ? tabs[0] : tabs[tabs.length - 1];
        if (next) focusTab(next.uid);
        return;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        onActivate(tab.uid);
        return;
      case 'Delete':
        if (tab.closable) {
          e.preventDefault();
          const neighbour = tabs[i + 1] ?? tabs[i - 1];
          onClose(tab.uid);
          if (neighbour) focusTab(neighbour.uid);
        }
        return;
      default:
    }
  };

  // ------------------------------------------------------------ render
  const isMain = variant === 'main';
  const moveLeftHint = isMac ? '⌥⇧←' : 'Alt+Shift+←';
  const moveRightHint = isMac ? '⌥⇧→' : 'Alt+Shift+→';

  return (
    <div
      className={props.className}
      style={props.style}
      data-testid={props.testId}
    >
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        className="relative flex min-w-0 flex-1 items-end gap-1 overflow-x-auto"
        onDragEnter={onListDragEnter}
        onDragOver={onListDragOver}
        onDragLeave={onListDragLeave}
        onDrop={onListDrop}
        data-testid={`${p}-list`}
      >
        {tabs.map((tab, i) => {
          const isActive = tab.uid === activeUid;
          const compact = tab.pinned;
          const shortcut = i < 9 ? ` (${mod}${i + 1})` : '';
          return (
            <div
              key={tab.uid}
              ref={(el) => {
                if (el) tabEls.current.set(tab.uid, el);
                else tabEls.current.delete(tab.uid);
              }}
              role="tab"
              aria-selected={isActive}
              aria-controls={tab.panelId}
              id={tab.domId}
              tabIndex={tab.uid === rovingUid ? 0 : -1}
              data-testid={tab.testId}
              data-tab-key={tab.key}
              data-pinned={tab.pinned ? 'true' : undefined}
              data-home={tab.home ? 'true' : undefined}
              draggable
              onDragStart={(e) => onTabDragStart(e, tab)}
              onDragEnd={onTabDragEnd}
              onFocus={() => setFocusUid(tab.uid)}
              onClick={() => onActivate(tab.uid)}
              onAuxClick={(e) => {
                if (e.button === 1 && tab.closable) {
                  e.preventDefault();
                  onClose(tab.uid);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenuFor(tab.uid);
              }}
              onKeyDown={(e) => onTabKeyDown(e, tab, i)}
              title={`${tab.label}${shortcut}`}
              className={`group flex flex-shrink-0 cursor-pointer select-none items-center gap-1 rounded-t-md border border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                isMain ? 'h-[30px] text-[13px]' : 'py-1.5 text-sm'
              } ${compact ? 'max-w-[9rem] pl-2 pr-1' : isMain ? 'max-w-[13rem] pl-3 pr-1' : 'max-w-[14rem] pl-3 pr-1'} ${
                isActive
                  ? 'border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100'
                  : 'border-transparent text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
              }`}
            >
              {tab.home ? (
                <Home className="h-3 w-3 flex-shrink-0 opacity-70" aria-hidden />
              ) : tab.pinned ? (
                <Pin className="h-3 w-3 flex-shrink-0 opacity-70" aria-hidden />
              ) : null}
              <span className={`truncate ${compact ? 'max-w-[6rem]' : ''}`}>{tab.label}</span>
              {tab.pinned && <span className="sr-only">{t('tabStrip.pinned', '(pinned)')}</span>}
              {/* The menu is portaled, but React events still bubble to the tab: keep its
                  clicks and keys (arrows, Enter) from activating / refocusing the tab. */}
              <span className="contents" onClick={stop} onKeyDown={stop} onAuxClick={stop} onContextMenu={stop}>
              <DropdownMenu
                open={menuFor === tab.uid}
                onOpenChange={(o) => setMenuFor(o ? tab.uid : null)}
              >
                {/* Radix opens a menu on POINTERDOWN (and prevents its default). This
                    button sits in the middle of the tab — invisible, but there, on every
                    inactive tab — so a press that began a drag there opened a modal menu
                    and cancelled the drag: the tab never moved, and the page took no
                    pointer events until the menu was dismissed. So the trigger never
                    sees pointerdown (stopped here, default NOT prevented, so the drag
                    starts) and the menu opens on click; a drag is never followed by a
                    click. Keyboard opening (Shift+F10, context-menu key) is unchanged. */}
                <span className="contents" onPointerDownCapture={stop}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setMenuFor(tab.uid)}
                      className={`rounded p-0.5 hover:bg-slate-300 focus:opacity-100 dark:hover:bg-slate-700 ${
                        isActive ? 'opacity-70' : 'opacity-0 group-hover:opacity-70'
                      }`}
                      aria-label={t('mainTabs.tab_menu', { defaultValue: 'Options for {{label}}', label: tab.label })}
                      data-testid={tab.menuTestId}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                </span>
                <DropdownMenuContent align="start" className="w-60">
                  {menuExtras?.(tab)}
                  {tab.movable && (
                    <>
                      <DropdownMenuItem className="gap-2" onSelect={() => onMoveToNewWindow(tab.uid)} data-testid={`${p}-move-window`}>
                        <AppWindow className="h-4 w-4" />
                        {t('mainTabs.move_to_window', 'Move to new window')}
                      </DropdownMenuItem>
                      {targets.length > 0 && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="gap-2" data-testid={`${p}-move-to`}>
                            <MoveRight className="h-4 w-4" />
                            {t('tabStrip.move_to', 'Move to')}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-60">
                            <DropdownMenuItem className="gap-2" onSelect={() => onMoveToNewWindow(tab.uid)} data-testid={`${p}-move-to-new`}>
                              <AppWindow className="h-4 w-4" />
                              {t('tabStrip.new_window', 'New window')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {targets.map((target) => (
                              <DropdownMenuItem
                                key={target.id}
                                className="gap-2"
                                onSelect={() => onMoveToTarget(tab.uid, target)}
                                data-testid={`${p}-move-to-${target.id}`}
                              >
                                {target.kind === 'main' ? <Home className="h-4 w-4" /> : <AppWindow className="h-4 w-4" />}
                                <span className="truncate">
                                  {target.kind === 'main'
                                    ? t('tabStrip.main_window', 'Main window')
                                    : target.title || t('tabStrip.untitled_window', 'Tabbed window')}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={!canMoveTab(tabs, tab.uid, -1)}
                    onSelect={() => move(tab.uid, i - 1)}
                    data-testid={`${p}-move-left`}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('tabStrip.move_left', 'Move left')}
                    {isActive && <DropdownMenuShortcut>{moveLeftHint}</DropdownMenuShortcut>}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={!canMoveTab(tabs, tab.uid, 1)}
                    onSelect={() => move(tab.uid, i + 1)}
                    data-testid={`${p}-move-right`}
                  >
                    <ArrowRight className="h-4 w-4" />
                    {t('tabStrip.move_right', 'Move right')}
                    {isActive && <DropdownMenuShortcut>{moveRightHint}</DropdownMenuShortcut>}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={!canMoveTab(tabs, tab.uid, -1)}
                    onSelect={() => move(tab.uid, 0)}
                    data-testid={`${p}-move-start`}
                  >
                    <ArrowLeftToLine className="h-4 w-4" />
                    {t('tabStrip.move_start', 'Move to start')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={!canMoveTab(tabs, tab.uid, 1)}
                    onSelect={() => move(tab.uid, tabs.length)}
                    data-testid={`${p}-move-end`}
                  >
                    <ArrowRightToLine className="h-4 w-4" />
                    {t('tabStrip.move_end', 'Move to end')}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onSelect={() => pin(tab, !tab.pinned)} data-testid={`${p}-pin`}>
                    {tab.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                    {tab.pinned ? t('tabStrip.unpin', 'Unpin tab') : t('tabStrip.pin', 'Pin tab')}
                  </DropdownMenuItem>
                  {(onCloseOthers || tab.closable) && <DropdownMenuSeparator />}
                  {onCloseOthers && (
                    <DropdownMenuItem
                      className="gap-2"
                      disabled={onlyOne}
                      onSelect={() => onCloseOthers(tab.uid)}
                      data-testid={`${p}-close-others`}
                    >
                      <XCircle className="h-4 w-4" />
                      {t('mainTabs.close_others', 'Close other tabs')}
                    </DropdownMenuItem>
                  )}
                  {tab.closable && (
                    <DropdownMenuItem className="gap-2" onSelect={() => onClose(tab.uid)} data-testid={`${p}-close-item`}>
                      <X className="h-4 w-4" />
                      {t('appPopout.close_tab', 'Close tab')}
                      {isDesktop && <DropdownMenuShortcut>{isMac ? '⌘W' : 'Ctrl+W'}</DropdownMenuShortcut>}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </span>
              {tabExtras?.(tab)}
              {/* Pinned tabs stay compact: close them from their menu (or middle click). */}
              {tab.closable && !tab.pinned && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(tab.uid);
                  }}
                  className="rounded p-0.5 hover:bg-slate-300 dark:hover:bg-slate-700"
                  aria-label={t('appPopout.close_tab', 'Close tab')}
                  title={t('appPopout.close_tab', 'Close tab')}
                  data-testid={tab.closeTestId}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
        {drop && (
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 top-1 w-0.5 rounded bg-blue-500"
            style={{ left: drop.x }}
            data-testid={`${p}-drop-indicator`}
          />
        )}
      </div>
      {addControl}
      <div aria-live="polite" role="status" className="sr-only" data-testid={`${p}-live`}>
        {announcement}
      </div>
    </div>
  );
}

export default TabStrip;
